import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe, formatDate } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button, Modal, SegmentedFilterControl } from '@spirit-testing/component-library';
import { catchError, finalize, map, of, startWith, switchMap } from 'rxjs';
import { ReleaseReportService } from '../../../services/release-report.service';
import { ReleaseResultsService } from '../../../services/release-results.service';
import { ReleasePlanResult } from '../../../models/release-results.model';
import { saveBlob } from '../../../shared/file-download';
import {
  ReleaseReportPreview,
  ReleaseReportRequest,
  ReportArt,
  ReportFormat,
  ReportPreviewScope,
  ReportUmfang,
  ReportVersion,
  ReportZeitraum,
} from '../../../models/release-report.model';
import { WaehlbareTeststufe } from '../test-results.utils';
import { ReportDocAbschlussComponent } from './report-doc-abschluss.component';
import { ReportDocStatusComponent } from './report-doc-status.component';
import { ReportDocTraceComponent } from './report-doc-trace.component';

interface PreviewState {
  loading: boolean;
  error: string | null;
  data: ReleaseReportPreview | null;
}

interface PreviewRequest {
  visible: boolean;
  projectId: string;
  releaseId: string;
  art: ReportArt;
  umfang: ReportUmfang;
  planId: string | null;
  scope: ReportPreviewScope;
  attempt: number;
}

interface PlansState {
  status: 'loading' | 'error' | 'loaded';
  plans: ReleasePlanResult[];
}

interface PlansRequest {
  visible: boolean;
  projectId: string;
  releaseId: string;
  attempt: number;
}

interface VersionsState {
  status: 'loading' | 'error' | 'loaded';
  releaseId: string | null;
  versions: ReportVersion[];
  refresh: number;
}

interface VersionsRequest {
  visible: boolean;
  projectId: string;
  releaseId: string;
  refresh: number;
}

interface Confirmation {
  filename: string;
  /** Stand der Versionsliste, der die gespeicherte Version enthält; `null`, wenn nichts gespeichert wurde. */
  savedInRefresh: number | null;
}

/** Die Blätter der Datei, die `ReleaseReportXlsxWriter` schreibt. */
const SHEETS: Record<ReportArt, string[]> = {
  STATUS: ['summary', 'planProgress', 'blocker'],
  ABSCHLUSS: ['summary', 'planResults', 'openDefects', 'deviations', 'traceability'],
  TRACEABILITY: ['traceability'],
};

const ART_ICON: Record<ReportArt, string> = { STATUS: 'bar_chart', ABSCHLUSS: 'shield', TRACEABILITY: 'link' };
const STAND_FORMAT = 'dd.MM.yyyy, HH:mm';
const IDLE: PreviewState = { loading: false, error: null, data: null };
const PLANS_LOADING: PlansState = { status: 'loading', plans: [] };

/** Report erzeugen (export-report.jsx:95-403). */
@Component({
  selector: 'app-report-modal',
  imports: [
    Button,
    DatePipe,
    MatIcon,
    Modal,
    SegmentedFilterControl,
    TranslatePipe,
    ReportDocAbschlussComponent,
    ReportDocStatusComponent,
    ReportDocTraceComponent,
  ],
  templateUrl: './report-modal.component.html',
  styleUrl: './report-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportModalComponent {
  private readonly reportService = inject(ReleaseReportService);
  private readonly resultsService = inject(ReleaseResultsService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly releaseId = input.required<string>();
  readonly releaseLabel = input.required<string>();
  readonly visible = model(false);
  /** Vorwahl aus der Sicht, gilt bei jedem Öffnen neu. */
  readonly initialArt = input<ReportArt | null>(null);
  /** Plan-Filter der Sicht: Kennung oder Schlüssel des Plans. */
  readonly initialPlan = input<string | null>(null);
  readonly canSaveVersion = input(false);
  readonly stufen = input<WaehlbareTeststufe[]>([]);
  /** Stufe der Werkzeugleiste, gilt bei jedem Öffnen neu. */
  readonly initialStufe = input<string | null>(null);

  protected readonly arts: ReportArt[] = ['STATUS', 'ABSCHLUSS', 'TRACEABILITY'];
  protected readonly artIcon = ART_ICON;

  protected readonly art = signal<ReportArt>('STATUS');
  protected readonly umfang = signal<ReportUmfang>('RELEASE');
  protected readonly zeitraum = signal<ReportZeitraum>('SEIT_LETZTEM');
  protected readonly stufe = signal<string | null>(null);
  protected readonly format = signal<ReportFormat>('PDF');
  private readonly saveChosen = signal(true);
  private readonly chosenPlan = signal<string | null>(null);

  private readonly plansAttempt = signal(0);
  private readonly plansState = signal<PlansState>(PLANS_LOADING);
  protected readonly plans = computed(() => this.plansState().plans);
  protected readonly preview = signal<PreviewState>(IDLE);
  private readonly previewAttempt = signal(0);

  protected readonly selectedPlan = computed(() => {
    const plans = this.plans();
    const chosen = this.chosenPlan();
    return plans.find((p) => chosen !== null && (p.id === chosen || p.key === chosen)) ?? plans[0] ?? null;
  });
  private readonly selectedPlanId = computed(() =>
    this.umfang() === 'PLAN' ? (this.selectedPlan()?.id ?? null) : null,
  );

  protected readonly data = computed(() => this.preview().data);
  private readonly planStatusInScope = computed(() => (this.umfang() === 'PLAN' ? this.plansState().status : null));
  protected readonly loading = computed(() => this.preview().loading || this.planStatusInScope() === 'loading');
  protected readonly plansFailed = computed(() => this.planStatusInScope() === 'error');
  protected readonly isEmpty = computed(() => {
    const d = this.data();
    if (!d) return false;
    if (d.art === 'TRACEABILITY') return (d.traceability ?? []).length === 0;
    if (d.art === 'STATUS') return !d.status || d.status.gesamt === 0;
    return !d.abschluss || (d.abschluss.gesamt === 0 && d.abschluss.traceability.length === 0);
  });
  protected readonly fromView = computed(
    () => !!this.initialPlan() || (!!this.initialStufe() && this.art() === 'STATUS'),
  );
  protected readonly scopeLabel = computed(() => {
    const plan = this.umfang() === 'PLAN' ? this.selectedPlan() : null;
    return plan ? (plan.key ?? plan.titel) : this.releaseLabel();
  });
  protected readonly sheets = computed(() => SHEETS[this.art()]);
  protected readonly saveActive = computed(() => this.canSaveVersion() && this.saveChosen());

  protected readonly generating = signal(false);
  protected readonly generateError = signal<string | null>(null);
  protected readonly confirmation = signal<Confirmation | null>(null);
  protected readonly canGenerate = computed(
    () => !this.generating() && !this.loading() && (this.umfang() === 'RELEASE' || this.selectedPlan() !== null),
  );

  private readonly versionsRefresh = signal(0);
  protected readonly versionsState = signal<VersionsState>({
    status: 'loading',
    releaseId: null,
    versions: [],
    refresh: 0,
  });
  protected readonly downloadingVersion = signal<number | null>(null);
  protected readonly versionError = signal<string | null>(null);
  /** Die Antwort auf das Speichern nennt die Nummer nicht, sie steht erst in der neu geladenen Liste. */
  protected readonly savedVersion = computed(() => {
    const confirmation = this.confirmation();
    const { status, versions, refresh } = this.versionsState();
    if (confirmation?.savedInRefresh == null || status !== 'loaded' || refresh < confirmation.savedInRefresh) {
      return null;
    }
    return versions.find((v) => v.dateiname === confirmation.filename)?.version ?? null;
  });

  private readonly previewScope = computed<ReportPreviewScope>(() => {
    if (this.art() !== 'STATUS') return {};
    const teststufe = this.stufe();
    return { zeitraum: this.zeitraum(), ...(teststufe ? { teststufe } : {}) };
  });

  private readonly previewRequest = computed<PreviewRequest>(() => ({
    visible: this.visible(),
    projectId: this.projectId(),
    releaseId: this.releaseId(),
    art: this.art(),
    umfang: this.umfang(),
    planId: this.selectedPlanId(),
    scope: this.previewScope(),
    attempt: this.previewAttempt(),
  }));

  private readonly plansRequest = computed<PlansRequest>(() => ({
    visible: this.visible(),
    projectId: this.projectId(),
    releaseId: this.releaseId(),
    attempt: this.plansAttempt(),
  }));

  private readonly versionsRequest = computed<VersionsRequest>(() => ({
    visible: this.visible(),
    projectId: this.projectId(),
    releaseId: this.releaseId(),
    refresh: this.versionsRefresh(),
  }));

  constructor() {
    let wasVisible = false;
    effect(() => {
      const open = this.visible();
      if (open && !wasVisible) {
        untracked(() => this.applyView());
      } else if (!open && wasVisible) {
        this.generateError.set(null);
        this.confirmation.set(null);
        this.versionError.set(null);
      }
      wasVisible = open;
    });

    toObservable(this.plansRequest)
      .pipe(
        switchMap((req) =>
          req.visible
            ? this.resultsService.getPlans(req.projectId, req.releaseId, {}).pipe(
                map((plans): PlansState => ({ status: 'loaded', plans })),
                catchError(() => of<PlansState>({ status: 'error', plans: [] })),
                startWith(PLANS_LOADING),
              )
            : of(null),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        if (state) this.plansState.set(state);
      });

    toObservable(this.previewRequest)
      .pipe(
        switchMap((req) => {
          if (!req.visible || (req.umfang === 'PLAN' && !req.planId)) return of<PreviewState>(IDLE);
          const previous = this.preview().data;
          return this.reportService.getPreview(req.projectId, req.releaseId, req.art, req.planId, req.scope).pipe(
            map((data): PreviewState => ({ loading: false, error: null, data })),
            catchError((err: unknown) =>
              of<PreviewState>({ loading: false, error: this.messageFor(err, 'preview'), data: null }),
            ),
            startWith<PreviewState>({ loading: true, error: null, data: previous }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => this.preview.set(state));

    toObservable(this.versionsRequest)
      .pipe(
        switchMap((req) => {
          if (!req.visible) return of(null);
          const { refresh, releaseId } = req;
          const shown = this.versionsState();
          const versions = shown.releaseId === releaseId ? shown.versions : [];
          return this.reportService.listVersions(req.projectId, req.releaseId).pipe(
            map((loaded): VersionsState => ({ status: 'loaded', releaseId, versions: loaded, refresh })),
            catchError(() => of<VersionsState>({ status: 'error', releaseId, versions: [], refresh })),
            startWith<VersionsState>({ status: 'loading', releaseId, versions, refresh }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((state) => {
        if (state) this.versionsState.set(state);
      });
  }

  protected subtitleText(): string {
    const stand = this.data()?.stand;
    const stufe = this.data()?.status?.teststufe;
    return [
      this.translate.instant('testResults.report.subtitle'),
      this.scopeLabel(),
      ...(stufe ? [this.translate.instant('testResults.report.doc.status.stage', { stufe })] : []),
      ...(stand ? [this.translate.instant('testResults.report.stand', { date: this.standText(stand) })] : []),
    ].join(' · ');
  }

  protected standText(stand: string): string {
    return formatDate(stand, STAND_FORMAT, 'en');
  }

  protected generate(): void {
    if (!this.canGenerate()) return;
    const plan = this.umfang() === 'PLAN' ? this.selectedPlan() : null;
    const save = this.saveActive();
    const request: ReleaseReportRequest = {
      art: this.art(),
      umfang: this.umfang(),
      ...(plan ? { planId: plan.id } : {}),
      format: this.format(),
      ...this.previewScope(),
      alsVersionSpeichern: save,
    };
    this.generating.set(true);
    this.generateError.set(null);
    this.confirmation.set(null);
    this.reportService
      .generate(this.projectId(), this.releaseId(), request, plan?.key ?? null)
      .pipe(
        finalize(() => this.generating.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ blob, filename }) => {
          saveBlob(blob, filename);
          const refresh = this.versionsRefresh() + 1;
          this.confirmation.set({ filename, savedInRefresh: save ? refresh : null });
          if (save) {
            this.versionsRefresh.set(refresh);
            this.retryPreview();
          }
        },
        error: (err: unknown) =>
          this.generateError.set(this.messageFor(err, 'generate', save ? 'forbiddenSave' : 'forbidden')),
      });
  }

  protected downloadVersion(version: ReportVersion): void {
    this.downloadingVersion.set(version.version);
    this.versionError.set(null);
    this.reportService
      .downloadVersion(this.projectId(), this.releaseId(), version)
      .pipe(
        finalize(() => this.downloadingVersion.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ blob, filename }) => saveBlob(blob, filename),
        error: (err: unknown) => this.versionError.set(this.messageFor(err, 'versionDownload')),
      });
  }

  protected retryPreview(): void {
    this.previewAttempt.update((attempt) => attempt + 1);
  }

  protected retryPlans(): void {
    this.plansAttempt.update((attempt) => attempt + 1);
  }

  protected retryVersions(): void {
    this.versionsRefresh.update((refresh) => refresh + 1);
  }

  protected toggleSave(): void {
    this.saveChosen.update((on) => !on);
  }

  protected choosePlan(id: string): void {
    this.chosenPlan.set(id);
  }

  protected chooseUmfang(value: string | null): void {
    if (value) this.umfang.set(value as ReportUmfang);
  }

  protected chooseZeitraum(value: string | null): void {
    if (value) this.zeitraum.set(value as ReportZeitraum);
  }

  protected chooseStufe(name: string): void {
    this.stufe.set(name || null);
  }

  protected chooseFormat(value: string | null): void {
    if (value) this.format.set(value as ReportFormat);
  }

  protected planLabel(plan: ReleasePlanResult): string {
    return plan.key ? `${plan.key} · ${plan.titel}` : plan.titel;
  }

  protected close(): void {
    this.visible.set(false);
  }

  private applyView(): void {
    const art = this.initialArt();
    const plan = this.initialPlan();
    if (art) this.art.set(art);
    this.umfang.set(plan ? 'PLAN' : 'RELEASE');
    this.stufe.set(this.initialStufe());
    if (plan) this.chosenPlan.set(plan);
  }

  private messageFor(
    err: unknown,
    kind: 'preview' | 'generate' | 'versionDownload',
    forbidden: 'forbidden' | 'forbiddenSave' = 'forbidden',
  ): string {
    if (err instanceof HttpErrorResponse && err.status === 403) {
      return this.translate.instant(`testResults.report.errors.${forbidden}`);
    }
    const serverMessage = err instanceof HttpErrorResponse ? err.error?.error : null;
    return typeof serverMessage === 'string' && serverMessage.length > 0
      ? serverMessage
      : this.translate.instant(`testResults.report.errors.${kind}`);
  }
}
